import {ContractType, Dictionary, MortgageContract, MortgageMonth, PaymentOverview, PaymentPlanType} from '../state/AppState';
import {calculateChain as linearChain, linearZeroMonth} from './calculators/linear.model';
import {lazy} from './utils/lazy';
import {annuityZeroMonth, calculateChain as annuityChain} from './calculators/annuity.model';
import {calculateChain as savingChain, savingZeroMonth} from './calculators/saving.model';
import {calculateChain as interestChain, interestZeroMonth} from './calculators/interest.model';
import {Dynamiclist, dynamicMapAtIndex, dynamicMapFromIndex, take} from './calculators/dynamiclist';
import {toArray} from './calculators/lazylist';


export function calculateMortgagesAsArray(contract: MortgageContract, payments?: Dictionary<PaymentOverview>): MortgageMonth[] {
  let mortgages = calculateMortgages(contract, payments);
  return toArray(mortgages);
}

function calculateMortgages(contract: MortgageContract, payments?: Dictionary<PaymentOverview>): Dynamiclist<MortgageMonth> {
  let mortgages = calculateMortgageList(contract);

  if (payments) {
    mortgages = applyPayments(mortgages, payments);
  }

  return mortgages;
}

function calculateMortgageList(contract: MortgageContract): Dynamiclist<MortgageMonth> {
  switch (contract.contractType) {
    case  ContractType.LINEAR:
      return calculateLineair(contract);
    case ContractType.SAVING:
      return calculateSaving(contract); // TODO: round the results
    case ContractType.ANNUITY_MONTHLY:
      return calculateAnnuity(contract);
    case ContractType.INTEREST_ONLY:
      return calculateInterest(contract);

    default:
      return () => null;
  }
}

function calculateLineair(contract: MortgageContract): Dynamiclist<MortgageMonth> {
  return linearChain(linearZeroMonth(contract), contract.durationMonths);
}

function calculateSaving(contract: MortgageContract): Dynamiclist<MortgageMonth> {
  return savingChain(lazy(contract.interestRate))(savingZeroMonth(contract), contract.durationMonths);
}

function calculateAnnuity(contract: MortgageContract): Dynamiclist<MortgageMonth> {
  return annuityChain(annuityZeroMonth(contract), contract.durationMonths);
}

function calculateInterest(contract: MortgageContract): Dynamiclist<MortgageMonth> {
  return take(481,  // TODO: size should come from somewhere else , now set to 40 yr
    interestChain(lazy(contract.interestRate))
    (interestZeroMonth(contract))
  );
}

function applyPayments(originalMortgage: Dynamiclist<MortgageMonth>, payments: Dictionary<PaymentOverview>): Dynamiclist<MortgageMonth> {
  let mortgages = originalMortgage;
  Object.values(payments).forEach(payment => {
      mortgages = applyPayment(mortgages, payment);
    }
  );
  return mortgages;
}

export function applyPayment(originalMortgage: Dynamiclist<MortgageMonth>, payment: PaymentOverview): Dynamiclist<MortgageMonth> {
  switch (payment.plan.planType) {
    case PaymentPlanType.ONCE:
      return applyPaymentOnce(originalMortgage, payment);
    case PaymentPlanType.MONTHLY:
      return applyPaymentMonthly(originalMortgage, payment);
    case PaymentPlanType.YEARLY:
      return applyPaymentYearly(originalMortgage, payment);
    default:
      return originalMortgage;
  }
}


function applyPaymentOnce(originalMortgage: Dynamiclist<MortgageMonth>, payment: PaymentOverview): Dynamiclist<MortgageMonth> {
  let mortgages = originalMortgage;
  const desiredPayment = payment.plan.amount;

  mortgages = dynamicMapAtIndex(
    (mortgage) => {
      const paymentAmount = (desiredPayment > mortgage.currentRemainder) ? mortgage.currentRemainder : desiredPayment;
      return {
        ...mortgage,
        newPrincipalPayment: mortgage.newPrincipalPayment + paymentAmount,
        currentTotalPrincipalPayment: mortgage.currentTotalPrincipalPayment + paymentAmount,
        currentRemainder: mortgage.currentRemainder - paymentAmount
      };
    },
    payment.plan.startAtMonth,
    mortgages
  );

  return mortgages;
}

function applyPaymentMonthly(originalMortgage: Dynamiclist<MortgageMonth>, payment: PaymentOverview): Dynamiclist<MortgageMonth> {
  let mortgages = originalMortgage;
  const desiredPayment = payment.plan.amount;

  mortgages = dynamicMapFromIndex(
    (mortgage) => {
      const paymentAmount = (desiredPayment > mortgage.currentRemainder) ? mortgage.currentRemainder : desiredPayment;
      return {
        ...mortgage,
        newPrincipalPayment: mortgage.newPrincipalPayment + paymentAmount,
        currentTotalPrincipalPayment: mortgage.currentTotalPrincipalPayment + paymentAmount,
        currentRemainder: mortgage.currentRemainder - paymentAmount
      };
    },
    payment.plan.startAtMonth,
    mortgages
  );

  return mortgages;
}

function applyPaymentYearly(originalMortgage: Dynamiclist<MortgageMonth>, payment: PaymentOverview): Dynamiclist<MortgageMonth> {
  //TODO: implement
  return originalMortgage;
}
